/**
 * Created by calhosh on 3/30/2026
 */
import {misstatementPeriodTypes, MisstatementType, ParentMisstatementType} from '../../../../../../../util/uiconstants';
import {currentResource} from '../../../../../../../util/utils';

export const filterMisstatementType = {
	sad: 1,
	incomeEffect: 2,
	reclassification: 3,
	cashflow: 4,
	disclosure: 5,
	manualAdjustment: 6
};

export const parentMisstatementType = {
	parentIncomeStatement: 7,
	parentReclassification: 8,
	parentCashFlow: 9,
	parentDisclosure: 10,
	parentManualTopUpAdjustment: 11
};

export const sadSummaryMaterialityViewTypes = {
	uncorrected: 3,
	corrected: 2,
	clearlyTrivial: 4
};

export const misstatementPeriodTypeId = {
	currentYear: 1,
	previousYear: 2
};

export const getTabSwitchData = () => {
	return [
		{
			id: sadSummaryMaterialityViewTypes.uncorrected,
			name: currentResource?.SadSummary304?.uncorrected,
			ariaLabel: currentResource?.SadSummary304?.uncorrected
		},
		{
			id: sadSummaryMaterialityViewTypes.corrected,
			name: currentResource?.SadSummary304?.corrected,
			ariaLabel: currentResource?.SadSummary304?.corrected
		}
	];
};

export const getPeriodTypeDisplayName = (periodTypeId) => {
	const periodTypeLabels = currentResource?.MisstatementDetails?.periodTypeLabels;

	switch (periodTypeId) {
		case misstatementPeriodTypes.currentPeriod:
			return periodTypeLabels?.currentPeriod;
		case misstatementPeriodTypes.comparativePeriod:
			return periodTypeLabels?.comparativePeriod;
		case misstatementPeriodTypes.secondComparativePeriod:
			return periodTypeLabels?.secondComparativePeriod;
		default:
			return null;
	}
};

export const getMisstatementTypeDisplayName = (misstatementTypeId) => {
	const misstatementTypeLabels = currentResource?.MisstatementDetails?.misstatementTypeLabels;

	switch (misstatementTypeId) {
		case MisstatementType.factual:
			return misstatementTypeLabels?.factual;
		case MisstatementType.projected:
			return misstatementTypeLabels?.projected;
		case MisstatementType.judgemental:
			return misstatementTypeLabels?.judgemental;
		case MisstatementType.reclassification:
			return misstatementTypeLabels?.reclassification;
		case MisstatementType.clientAdjustment:
			return misstatementTypeLabels?.clientAdjustment;
		case MisstatementType.manualTopUpAdjustment:
			return misstatementTypeLabels?.manualTopUpAdjustment;
		case MisstatementType.notSelected:
			return misstatementTypeLabels?.notSelected;
		default:
			return null;
	}
};

export const getParentMisstatementTypeDisplayName = (parentMisstatementTypeId) => {
	const parentMisstatementTypeLabels = currentResource?.MisstatementDetails?.parentMisstatementTypeLabels;

	switch (parentMisstatementTypeId) {
		case ParentMisstatementType.hasEffectOnIncome:
			return parentMisstatementTypeLabels?.hasEffectOnIncome;
		case ParentMisstatementType.doesNotHaveEffectOnIncome:
			return parentMisstatementTypeLabels?.doesNotHaveEffectOnIncome;
		case ParentMisstatementType.cashFlow:
			return parentMisstatementTypeLabels?.cashFlow;
		case ParentMisstatementType.disclosure:
			return parentMisstatementTypeLabels?.disclosure;
		default:
			return null;
	}
};

export const getAllMisstatementsForMetrics = (metrics, activeTab) => {
	if (!metrics) {
		return 0;
	}

	return activeTab === sadSummaryMaterialityViewTypes.corrected ? (metrics?.countCorrectedMisstatement ?? 0) : (metrics?.countUncorrectedMisstatement ?? 0);
};

// Misstatement calculation constants
export const analysisMisstatementPeriodTypeId = 1;
export const comparativeMisstatementPeriodTypeId = 2;
export const grossAmountEligibleFsTypeIds = [11, 20, 21];

// Misstatement calculation functions
export function calculateGrossAmount(item) {
	if (item.misstatementPeriodTypeId !== analysisMisstatementPeriodTypeId) {
		return 0;
	}
	const value = item?.adjustmentTotals?.analysisPeriodAdjustmentAmount;
	if (value === null || value === undefined) {
		return 0;
	}

	return Number(value);
}

export function calculateNetAmountAfterTurnaround(item) {
	if (item.misstatementPeriodTypeId === analysisMisstatementPeriodTypeId) {
		// CY or CY/PY misstatement: gross amount + turnaroundAmount from comparative children
		const grossAmt = calculateGrossAmount(item);

		// If childMisstatements exist, sum turnaroundAmount from their adjustments
		const hasChildren = item.childMisstatements && Array.isArray(item.childMisstatements) && item.childMisstatements.length > 0;
		if (hasChildren) {
			const childTurnaroundSum = item.childMisstatements.reduce((sum, child) => {
				const childAdjustments = child.misstatementAdjustments || [];
				const childSum = childAdjustments.reduce((adjSum, adj) => adjSum + (adj.turnaroundAmount || 0), 0);
				return sum + childSum;
			}, 0);
			return grossAmt + childTurnaroundSum;
		}

		// Fallback to grossAmount
		return grossAmt;
	}
	if (item.misstatementPeriodTypeId === comparativeMisstatementPeriodTypeId) {
		// PY misstatement: sum of turnaroundAmount from own adjustments
		return (item.misstatementAdjustments || []).reduce((sum, adj) => sum + (adj.turnaroundAmount || 0), 0);
	}
	return 0;
}

// Multi-entity helpers
export const getEntityIdFromMisstatement = (multiEntities) => {
	const entity = (multiEntities ?? [])[0];
	return entity?.stEntityId ?? entity?.id ?? entity?.entityId ?? null;
};

// Deficiency relationship helpers
export const relatedDeficiencyState = {
	related: 'related',
	noRelated: 'noRelated',
	hidden: 'hidden'
};

export function getRelatedDeficienciesCount(relatedDeficiencies) {
	if (!Array.isArray(relatedDeficiencies)) {
		return 0;
	}
	return relatedDeficiencies.length;
}

export function hasDeficiencyRationaleValue(deficiencyRationale) {
	if (deficiencyRationale === null || deficiencyRationale === undefined) {
		return false;
	}
	if (typeof deficiencyRationale === 'string') {
		return deficiencyRationale.trim().length > 0;
	}
	return true;
}

export function hasDisclosureEvaluationRationale(evaluationRationale) {
	if (evaluationRationale === null || evaluationRationale === undefined) {
		return false;
	}
	if (typeof evaluationRationale === 'string') {
		return evaluationRationale.trim().length > 0;
	}
	return true;
}

export function getMisstatementRelatedDeficiencyState(misstatement, hasMisstatementRelatedDeficiencyOption) {
	if (!hasMisstatementRelatedDeficiencyOption) {
		return relatedDeficiencyState.hidden;
	}

	if (getRelatedDeficienciesCount(misstatement?.deficiencies) > 0) {
		return relatedDeficiencyState.related;
	}

	if (hasDeficiencyRationaleValue(misstatement?.deficiencyRationale)) {
		return relatedDeficiencyState.noRelated;
	}

	return relatedDeficiencyState.hidden;
}
